/** Create a sprint — with its name and dates on screen before it exists.
 *
 * The New sprint button used to create one instantly, with no dialog and no message, and
 * leave the board where it was. On 2026-09-30 Brian pressed it three times wanting
 * "Sprint 4", saw nothing happen, and made three sprints named 22, 23 and 24. Now the
 * suggestion is shown first — the sprint after the active one, starting after whatever is
 * already planned and never in the past — and nothing exists until Create is pressed.
 */

import { useState } from 'react'

import { Button, DateInput, ErrorState, Field, inputClass } from '@/components/ui'
import { useCreateSprint, type Sprint } from '@/features/workbench/useWork'

const LENGTH_DAYS = 14
const NUMBERED = /^Sprint (\d+)$/

function iso(day: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${day.getFullYear()}-${pad(day.getMonth() + 1)}-${pad(day.getDate())}`
}

function addDays(isoDate: string, days: number): string {
  const day = new Date(`${isoDate}T00:00:00`)
  day.setDate(day.getDate() + days)
  return iso(day)
}

/** Mirrors the server's defaults (`next_sprint_name`, `next_sprint_start`), within one
 * team's own sprints — each team numbers and dates its own (2026-10-07). */
function suggestSprint(
  sprints: Sprint[],
  team: string,
): { name: string; start: string; end: string } {
  const native = sprints.filter((s) => !s.isExternal && (s.team ?? '') === team)
  const taken = new Set(native.map((s) => s.name))
  const numbers = native.map((s) => NUMBERED.exec(s.name)).filter(Boolean).map((m) => Number(m![1]))
  const active = native.find((s) => s.status === 'active')
  const activeNumber = active ? NUMBERED.exec(active.name) : null
  let n = activeNumber ? Number(activeNumber[1]) + 1 : numbers.length ? Math.max(...numbers) + 1 : 1
  while (taken.has(`Sprint ${n}`)) n += 1

  const today = iso(new Date())
  const openEnds = native.filter((s) => s.status !== 'completed').map((s) => s.endDate).sort()
  const afterLast = openEnds.length ? addDays(openEnds[openEnds.length - 1] as string, 1) : today
  const start = afterLast > today ? afterLast : today
  return { name: `Sprint ${n}`, start, end: addDays(start, LENGTH_DAYS - 1) }
}

export function NewSprintDialog({
  sprints,
  team: initialTeam,
  teams,
  onClose,
  onCreated,
}: {
  sprints: Sprint[]
  /** The board's team filter, if any — where the new sprint most likely belongs. */
  team: string
  teams: { team: string; shortLabel: string }[]
  onClose: () => void
  onCreated: (sprint: Sprint) => void
}) {
  const create = useCreateSprint()
  const [team, setTeam] = useState(initialTeam || 'it_operations')
  const [form, setForm] = useState(() => {
    const suggested = suggestSprint(sprints, initialTeam || 'it_operations')
    return { name: suggested.name, goal: '', start_date: suggested.start, end_date: suggested.end }
  })
  const chooseTeam = (next: string) => {
    setTeam(next)
    // Another team means another sequence: re-suggest its next name and dates.
    const suggested = suggestSprint(sprints, next)
    setForm((f) => ({ ...f, name: suggested.name, start_date: suggested.start, end_date: suggested.end }))
  }
  const datesInverted = Boolean(form.start_date && form.end_date && form.end_date < form.start_date)
  const nameTaken = sprints.some(
    (s) => !s.isExternal && (s.team ?? '') === team && s.name.trim() === form.name.trim(),
  )

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div
        className="w-full max-w-md rounded-lg bg-canvas p-6 shadow-2xl"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="New sprint"
      >
        <h2 className="mb-1 font-heading text-2xl text-fg">New sprint</h2>
        <p className="mb-4 text-sm text-fg-muted">
          Suggested: the team&apos;s next sprint, starting after anything it already has
          planned. Change anything before creating it.
        </p>

        {create.error ? (
          <div className="mb-4">
            <ErrorState error={create.error} />
          </div>
        ) : null}

        <div className="space-y-3">
          <Field label="Team" hint="Each team has its own sprints and its own active sprint.">
            <select className={inputClass} value={team} onChange={(e) => chooseTeam(e.target.value)}>
              {teams.map((t) => (
                <option key={t.team} value={t.team}>
                  {t.shortLabel}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Name" hint={nameTaken ? 'A sprint already has this name.' : undefined}>
            <input
              className={inputClass}
              value={form.name}
              autoFocus
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            />
          </Field>
          <Field label="Goal" hint="Optional — what this fortnight is for.">
            <input className={inputClass} value={form.goal} onChange={(e) => setForm((f) => ({ ...f, goal: e.target.value }))} />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Start date">
              <DateInput value={form.start_date} onChange={(v) => setForm((f) => ({ ...f, start_date: v }))} />
            </Field>
            <Field label="End date">
              <DateInput value={form.end_date} onChange={(v) => setForm((f) => ({ ...f, end_date: v }))} />
            </Field>
          </div>
          {datesInverted ? (
            <p className="text-xs" style={{ color: 'var(--status-red)' }}>
              The end date is before the start date.
            </p>
          ) : null}
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            disabled={!form.name.trim() || !form.start_date || !form.end_date || datesInverted || nameTaken || create.isPending}
            onClick={() =>
              create.mutate(
                {
                  name: form.name.trim(),
                  goal: form.goal.trim(),
                  team,
                  start_date: form.start_date,
                  end_date: form.end_date,
                },
                {
                  onSuccess: (sprint) => {
                    onCreated(sprint)
                    onClose()
                  },
                },
              )
            }
          >
            {create.isPending ? 'Creating…' : 'Create sprint'}
          </Button>
        </div>
      </div>
    </div>
  )
}
